import React, { forwardRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { LOAD_TYPE, LoadLocation } from '../../../redux/api/load/load.model';
import CreateLocation from '../../Locations/CreateLocation/CreateLocation';
import EditLocation from '../../Locations/EditLocation/EditLocation';

import {
  deleteSegment,
  updateStopV2,
  mergeAllSegments,
  updateDriverPayConfirmationModal
} from '../../../redux/features/orders/ordersSlice';
import { TwAutocompleteOption } from '../../../components/TwAutocomplete/TwAutocomplete';
import useTwLocation from '../../../hooks/useTwLocation';
import MergeTripsConfirmation from './MergeTripsConfirmation';
import DeleteTripConfirmation from './DeleteTripConfirmation';
import './RouteInfo.scss';

import TripsRoute from './TripsRoute';
import useAuth from '../../../providers/Auth/Auth.provider';
import { useLocationTypeOptions } from '../../../hooks/useLocationTypeOptions';
import useCheckDriverPay from '../../../hooks/useCheckDriverPay';
import { EntityType } from '../../../redux/Global.model';

interface RouteProps {
  isImport?: boolean;
  fullScreenImport?: boolean;
  createOrder?: boolean;
  routeContainerSx?: SxProps;
}

const Route = forwardRef<HTMLDivElement, RouteProps>(
  ({ isImport = false, fullScreenImport, createOrder, routeContainerSx }, ref) => {
    const { getTenantSetting } = useAuth();
    const dispatch = useAppDispatch();
    const loadState = useAppSelector((state) => state.orders);
    const isSplit = loadState.order_type === LOAD_TYPE.SEGMENT;
    const segments = loadState.trips;
    const stops = loadState.provider_stops;
    const isEditLoadOpen = useAppSelector((state) => state.navigation.isEditLoadOpen);
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const anyDriverPayExists = useCheckDriverPay({ entity: EntityType.ORDER });

    const [activeStopUiId, setActiveStopUiId] = useState<string | null>(null);

    const { locations: twLocations, fetchLocations } = useTwLocation(true);

    // Locations
    const [locations, setLocations] = useLocationTypeOptions(twLocations);
    const [hasLocationBeenUpdated, setHasLocationBeenUpdated] = useState<boolean>(false);
    const [blockFetchLocations, setBlockFetchLocations] = useState<boolean>(false);
    const [isEditLocationOpen, setIsEditLocationOpen] = useState<boolean>(false);
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    const [isCreateLocationOpen, setIsCreateLocationOpen] = useState<boolean>(false);
    const [openMergeTripsConfirmation, setOpenMergeTripsConfirmation] = useState<boolean>(false);
    const [tripToDelete, setTripToDelete] = useState<string>('');
    const [openDeleteTripConfirmation, setOpenDeleteTripConfirmation] = useState<boolean>(false);

    const isAutoCalculateMileageDisabled = getTenantSetting(
      'disable-auto-calculate-mileage',
      false
    );

    // #region Functions

    const handleEditLocation = (newLocation: LoadLocation, clearDriverPay: boolean): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newLocation?.id || '',
        label: newLocation?.name || '',
        value: newLocation
      };

      if (activeStopUiId) {
        const stopToUpdate = stops.find((stop) => stop.ui_id === activeStopUiId);
        if (stopToUpdate) {
          const newStop = { ...stopToUpdate };
          newStop.location_id = newLocation?.id;
          newStop.provider_location = newAutocompleteOption;

          if (
            newLocation.notes ||
            newLocation.contact_email ||
            newLocation.contact_name ||
            newLocation.contact_number ||
            newLocation.contact_number_ext
          ) {
            newStop.provider_include_location_notes = true;
            newStop.notes = newLocation.notes;
            newStop.contact = {
              contact_name: newLocation.contact_name,
              contact_number: newLocation.contact_number,
              contact_number_ext: newLocation.contact_number_ext,
              contact_email: newLocation.contact_email
            };
          } else {
            newStop.provider_include_location_notes = false;
            newStop.notes = '';
            newStop.contact = undefined;
          }

          dispatch(
            updateStopV2({
              stop: newStop,
              clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : clearDriverPay
            })
          );
          setHasLocationBeenUpdated(true);
        }
      }

      fetchLocations();
      setHasLocationBeenUpdated(false);
    };

    const handleCreateLocation = (newLocation: LoadLocation): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newLocation.id,
        label: newLocation.name,
        value: newLocation
      };

      setLocations((prevLocations) => [newAutocompleteOption, ...prevLocations]);
      if (activeStopUiId) {
        const stopToUpdate = stops.find((stop) => stop.ui_id === activeStopUiId);
        if (stopToUpdate) {
          const newStop = { ...stopToUpdate };
          newStop.location_id = newLocation.id;
          newStop.provider_location = newAutocompleteOption;
          newStop.location = newLocation;
          if (
            newLocation.notes ||
            newLocation.contact_email ||
            newLocation.contact_name ||
            newLocation.contact_number ||
            newLocation.contact_number_ext
          ) {
            newStop.provider_include_location_notes = true;
            newStop.notes = newLocation.notes;
          } else {
            newStop.provider_include_location_notes = false;
            newStop.notes = '';
          }

          newStop.contact = {
            contact_name: newLocation.contact_name,
            contact_number: newLocation.contact_number,
            contact_number_ext: newLocation.contact_number_ext,
            contact_email: newLocation.contact_email
          };

          dispatch(
            updateStopV2({
              stop: newStop,
              clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
            })
          );
        }
      }

      fetchLocations();
    };

    const handleEditLocationClose = (): void => {
      setSelectedLocationId('');
      setActiveStopUiId(null);
      setIsEditLocationOpen(false);

      const pacContainers = document.getElementsByClassName('pac-container');

      if (pacContainers.length > 0) {
        Array.from(pacContainers).forEach((pc) => pc.remove());
      }
    };

    const handleCreateLocationClose = (): void => {
      setActiveStopUiId(null);
      setIsCreateLocationOpen(false);

      const pacContainers = document.getElementsByClassName('pac-container');

      if (pacContainers.length > 0) {
        Array.from(pacContainers).forEach((pc) => pc.remove());
      }
    };

    // #endregion

    useEffect(() => {
      if (isEditLoadOpen) {
        setBlockFetchLocations(true);

        setTimeout(() => {
          setBlockFetchLocations(false);
        }, 500);
      }
    }, [isEditLoadOpen]);
    // #region Effects

    return (
      <>
        <Box
          ref={ref}
          component="div"
          className={`${
            (isImport && !fullScreenImport) || matchesMobileOrTablet
              ? 'route-info__main-import'
              : 'route-info__main'
          } `}
        >
          <span
            className={
              (isImport && !fullScreenImport) || matchesMobileOrTablet
                ? 'route-info__title-left'
                : 'route-info__title'
            }
          >
            Route
          </span>
          <span
            className={
              isImport || matchesMobileOrTablet
                ? 'route-info__sub-title-left'
                : 'route-info__sub-title'
            }
          >
            Add segments and stops to create your route.
          </span>
          <span
            className={
              isImport || matchesMobileOrTablet
                ? 'route-info__sub-title-left'
                : 'route-info__sub-title'
            }
          >
            A segment needs to have at least 2 stops
          </span>

          <Box sx={routeContainerSx}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography component="h5" className="route-info__info-title">
                Route information
              </Typography>

              {isSplit && (
                <Box
                  component="label"
                  className="route-info__action-label"
                  onClick={(): void => {
                    if (anyDriverPayExists) {
                      dispatch(
                        updateDriverPayConfirmationModal({
                          open: true,
                          action: () => setOpenMergeTripsConfirmation(true)
                        })
                      );
                    } else {
                      setOpenMergeTripsConfirmation(true);
                    }
                  }}
                >
                  Merge all trips
                </Box>
              )}
            </Box>

            {segments.map((segment, segmentIndex) => (
              <TripsRoute
                key={segment.ui_id}
                segment={segment}
                segmentIndex={segmentIndex}
                isImport={isImport}
                createOrder={createOrder}
                fullScreenImport={fullScreenImport}
                hasLocationBeenUpdated={hasLocationBeenUpdated}
                locations={locations}
                blockFetchLocations={blockFetchLocations}
                tripToDelete={setTripToDelete}
                handleOpenDeleteTripConfirmation={setOpenDeleteTripConfirmation}
                handleCreateLocationOpen={setIsCreateLocationOpen}
                handleLocationEditOpen={setIsEditLocationOpen}
                handleSelectedLocationId={setSelectedLocationId}
                handleActiveStopUiId={setActiveStopUiId}
                refetchLocations={fetchLocations}
              />
            ))}
          </Box>
        </Box>

        <CreateLocation
          open={isCreateLocationOpen}
          onClose={handleCreateLocationClose}
          onSuccess={handleCreateLocation}
          entity={EntityType.ORDER}
        />

        <EditLocation
          locationId={selectedLocationId}
          open={isEditLocationOpen}
          onClose={handleEditLocationClose}
          onSuccess={handleEditLocation}
          entity={EntityType.ORDER}
        />

        <MergeTripsConfirmation
          open={openMergeTripsConfirmation}
          onClose={(): void => setOpenMergeTripsConfirmation(false)}
          onMerge={(): void => {
            dispatch(mergeAllSegments(isAutoCalculateMileageDisabled));
            setOpenMergeTripsConfirmation(false);
          }}
        />

        <DeleteTripConfirmation
          open={openDeleteTripConfirmation}
          onClose={(): void => setOpenDeleteTripConfirmation(false)}
          onDelete={(): void => {
            dispatch(deleteSegment(tripToDelete));
            setOpenDeleteTripConfirmation(false);
          }}
        />
      </>
    );
  }
);
Route.displayName = 'RouteInfo';
export default Route;
